/**
 * 商談ゴミ箱画面 (Phase 9)。
 *
 * - _deleted=true の Deal を表示
 * - 30分以内: 「即時 undo」ボタン (本人 / editor+)
 * - 5営業日以内: 「管理者復元」(owner)
 * - 5営業日超過: purgeDeletedDeals が翌03:00に物理削除
 */
import { Trash2, Undo2 } from 'lucide-react';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import {
  useAdminRecoverDeal,
  useRestoreDeletedDeal,
  useTrashDeals,
} from '@/hooks/useDealUndo';

const IMMEDIATE_UNDO_MS = 30 * 60 * 1000;

function tsToMs(v: unknown): number | null {
  if (!v) return null;
  if (typeof v === 'string') return new Date(v).getTime();
  if (typeof v === 'object' && v !== null && 'toMillis' in v) {
    return (v as { toMillis: () => number }).toMillis();
  }
  return null;
}

function fmtDate(v: unknown): string {
  const ms = tsToMs(v);
  return ms ? new Date(ms).toLocaleString('ja-JP') : '-';
}

export function DealsTrashPage() {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const { data, isLoading } = useTrashDeals();
  const restore = useRestoreDeletedDeal();
  const adminRecover = useAdminRecoverDeal();

  const handleRestore = async (id: string) => {
    try {
      await restore.mutateAsync(id);
      toast.success('Deal を復元しました');
    } catch (err) {
      toast.error(`復元失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleAdminRecover = async (id: string) => {
    try {
      await adminRecover.mutateAsync(id);
      toast.success('管理者復元しました');
    } catch (err) {
      toast.error(`復元失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <Screen
      title="ゴミ箱 (商談)"
      description="削除済み商談の復元 / 物理削除待ち。30分以内なら即時 undo、5営業日以内なら管理者復元が可能。"
    >
      {isLoading ? (
        <Spinner />
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={Trash2}
          title="ゴミ箱は空です"
          description="削除済み Deal はありません。"
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border)] text-left">
                <th className="py-2 px-3">商談</th>
                <th className="py-2 px-3">削除日時</th>
                <th className="py-2 px-3">復元期限</th>
                <th className="py-2 px-3">状態</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.map((d) => {
                const deletedMs = tsToMs(d._deletedAt);
                const recoverMs = tsToMs(d._adminRecoverableUntil);
                const now = Date.now();
                const canImmediate =
                  deletedMs !== null && now - deletedMs <= IMMEDIATE_UNDO_MS;
                const canAdminRecover =
                  recoverMs !== null && now < recoverMs;
                return (
                  <tr
                    key={d.id}
                    className="border-b border-[var(--border)] hover:bg-[var(--bg-hover)]"
                  >
                    <td className="py-2 px-3 font-medium">{d.title}</td>
                    <td className="py-2 px-3 text-xs">{fmtDate(d._deletedAt)}</td>
                    <td className="py-2 px-3 text-xs">
                      {fmtDate(d._adminRecoverableUntil)}
                    </td>
                    <td className="py-2 px-3">
                      {canImmediate ? (
                        <Badge>30分以内 (undo 可)</Badge>
                      ) : canAdminRecover ? (
                        <Badge>管理者復元可</Badge>
                      ) : (
                        <Badge>期限切れ (近日物理削除)</Badge>
                      )}
                    </td>
                    <td className="py-2 px-3 space-x-1">
                      {canImmediate && (
                        <Button
                          type="button"
                          variant="primary"
                          size="sm"
                          onClick={() => handleRestore(d.id)}
                        >
                          <Undo2 />
                          即時 undo
                        </Button>
                      )}
                      {!canImmediate && canAdminRecover && isOwner && (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => handleAdminRecover(d.id)}
                        >
                          <Undo2 />
                          管理者復元
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Screen>
  );
}
